import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { defineStore } from 'pinia'
import { DOMAINS_PREFIXES, getDefaultAppAreaConfigByHostname, defineFunnelByParams } from '@/funnels/funnels.ts'
import { getPaywallRouteName } from '@/funnels/funnelUtils.ts'
import { findMatchingOverride } from '@/helpers/jsonConfig'
import paywallRoutes from '@/modules/Paywall/paywallRoutes.ts'
import { useMainStore } from './main'
import { useSurveyStore } from './survey'
import { weeklyStorage, permanentStorage, sharedKeyPersist } from './persistence'

function groupSearchParamsByKey(entries) {
	return [...entries].reduce((result, tuple) => {
		const [key, val] = tuple
		if (result.hasOwnProperty(key)) {
			if (Array.isArray(result[key])) {
				result[key] = [...result[key], val]
			} else {
				result[key] = [result[key], val]
			}
		} else {
			result[key] = val
		}

		return result
	}, {})
}

function getFunnelPrefixFromPath(path) {
	const index = DOMAINS_PREFIXES.findIndex(([, regexp]) => regexp.test(path))

	if (index !== -1) {
		return DOMAINS_PREFIXES[index][0]
	}
	return '/'
}

function transformQueryParam(paramName, paramValue) {
	if (paramValue === '' || paramValue === 'true') {
		paramValue = true
	} else if (paramValue === 'false') {
		paramValue = false
	}

	if (paramName === 'email' && typeof paramValue === 'string') {
		paramValue = paramValue.trim().replaceAll(' ', '+')
	}

	return paramValue
}

const initialQueryParams = new URLSearchParams(window.location.search)

export const useLocationStore = defineStore('location', {
	state: () => ({
		host: window.location.hostname,
		queryParams: {},
		initialPath: '',
		initialUtmParams: {},
		appAreaConfig: null,
		initialFunnelType: null,
		initialFunnelId: null,
		initialUserTags: null,
		paywallRoute: '',

		// DEV-35776: препарат, который рекламировал креатив, приведший на лендинг —
		// `landing.overrides[].medication` резолвнутого варианта (null на универсальном).
		// Это единственный мостик между словарём лендинга (`utm_term`) и словарём пейвола:
		// буквы креатива разбираются только в area-конфиге, а сюда попадает уже результат.
		// Едет в общем персистнутом блобе, поэтому переживает переход на пейвол, который
		// идёт без query. Второй и последний вход в воронку — рассылка через /redirect —
		// несёт свой явный `?medication=` и сюда не пишет.
		landingMedication: null,
	}),

	getters: {
		// `this`-using getters need explicit @returns annotations: without them TS can't
		// resolve the circular getters object and types the WHOLE getters bag as {} for
		// TS consumers of this store (first hit: src/modules/abPlatform.ts).
		/** @returns {string} */
		hostname() {
			let hostname = this.host

			if (ENV_DEV_OR_STAGING) {
				hostname = 'trykova.com'
			}

			return hostname
		},

		basePath() {
			// Must match the build's base (vite.config `base`) so deep links like
			// /survey/prescription-review resolve on every deployed host (trykova,
			// appmedcloud.dev/.live, *.workers.dev previews) — not just trykova.com.
			// Single source of truth.
			return import.meta.env.BASE_URL
		},

		/** @returns {() => string} */
		getPathname() {
			return () => window.location.pathname.replace(this.basePath, '/')
		},

		getQueryParam: (state) => (param) => {
			let method = 'get'
			if (param === 'exp') {
				method = 'getAll'
			}
			let value = transformQueryParam(param, initialQueryParams[method](param))
			if (Array.isArray(value) && value.length === 0) {
				value = null
			}
			return value ?? state.queryParams[param]
		},

		/** @returns {(param: string) => string | null | undefined} */
		getRawQueryParam() {
			return (param) => {
				const value = this.getQueryParam(param)
				if (typeof value === 'boolean') return null
				if (Array.isArray(value)) return value[0] ?? null
				return value
			}
		},

		/** @returns {boolean} */
		isPaidTraffic() {
			return Boolean(this.getQueryParam('utm_source'))
		},

		// Environment flag: this browsing session was opened from the mobile app's webview (the
		// app appends ?from=app to every link it opens) — same name and contract as
		// med-web-account's isWebview (its ADR 0015). Deliberately rides the EXISTING persisted
		// queryParams snapshot (shared 'vuex' blob, weeklyStorage — 7-day lscache TTL, sliding:
		// every persisted state change rewrites the blob), so the flag survives reloads and
		// in-app navigation. Accepted trade-offs (Android Custom Tab
		// storage sharing, the stale-flag window, shared links) are documented in
		// docs/intake-form.md §«Возврат в апп (webview) — DEV-35414» — read it before
		// "fixing" either here.
		/** @returns {boolean} */
		isWebview: (state) => state.queryParams.from === 'app',

		manageSubscriptionSource: (state) => state.queryParams.manage_subscription_source ?? 'web',

		/**
		 * @return {{web: boolean, android: boolean, ios: boolean}}
		 */
		accountPlatform() {
			return {
				ios: this.manageSubscriptionSource === 'ios',
				android: this.manageSubscriptionSource === 'android',
				web: this.manageSubscriptionSource !== 'ios' && this.manageSubscriptionSource !== 'android',
			}
		},

		/** @returns {object} */
		getAppAreaConfig() {
			return this.appAreaConfig || getDefaultAppAreaConfigByHostname(this.hostname)
		},

		/** @returns {string} */
		getPaywallRoute() {
			const planQueryParam = this.getQueryParam('plan')

			if (planQueryParam && paywallRoutes.map((route) => route.name).includes(planQueryParam)) {
				return planQueryParam
			}

			const config = this.getAppAreaConfig
			const survey = useSurveyStore()

			// Answer-driven paywall selection (e.g. semaglutide vs tirzepatide). Survey
			// answers aren't known at funnel-definition time, so — unlike the country
			// paywall overrides baked in defineFunnelByParams — these `{ matcher, routeName }`
			// overrides are matched here, at read time, once the user has answered. No
			// match (incl. no answer yet) falls through to the default routeName.
			//
			// `medication` is the RESOLVED medication, deliberately distinct from the raw
			// answer: an explicit pick on CreatedProgramView wins, a "recommend_me" answer
			// resolves via the remote flag (see survey.effectiveMedicationChoice). Raw
			// answers stay available under their own keys via the spread.
			const override = findMatchingOverride(config.paywall?.overrides, {
				...survey.values.data,
				medication: survey.effectiveMedicationChoice,
			})
			if (override?.routeName) {
				return override.routeName
			}

			return getPaywallRouteName(config)
		},

		getInitialPath(state) {
			return state.initialPath
		},

		isEmptyInitialUtmParams: (state) => {
			return Object.keys(state.initialUtmParams).length === 0
		},

		getInitialFunnelType: (state) => state.initialFunnelType,

		getInitialFunnelId: (state) => state.initialFunnelId,

		getInitialUserTags: (state) => state.initialUserTags,
	},

	actions: {
		storeQueryParams() {
			const params = groupSearchParamsByKey(initialQueryParams.entries())
			const normalizedParams = {}

			for (let param in params) {
				normalizedParams[param] = transformQueryParam(param, params[param])
			}

			this.queryParams = {
				...this.queryParams,
				...normalizedParams,
			}
		},

		setAppAreaConfig(appAreaConfig) {
			this.appAreaConfig = appAreaConfig
		},

		// Вызывается из LandingView сразу после резолва варианта. Универсальный лендинг
		// сбрасывает значение в null — «креатив препарат не называл», и дальше решает дефолт.
		setLandingMedication(medication) {
			this.landingMedication = medication ?? null
		},

		setInitialFunnelType(value) {
			this.initialFunnelType = value
		},

		setInitialFunnelId(value) {
			this.initialFunnelId = value
		},

		setInitialUserTags(value) {
			this.initialUserTags = value
		},

		storeInitialUtmParams(value) {
			this.initialUtmParams = value
		},

		// Drops a param from BOTH carriers `getQueryParam` reads. Clearing only the
		// persisted snapshot is not enough: `initialQueryParams` is captured once at
		// module load and an in-app navigation never re-reads the URL, so the value
		// would stay reachable for the rest of the visit.
		removeQueryParam(key) {
			initialQueryParams.delete(key)
			delete this.queryParams[key]
		},

		storeInitialPath({ force = false, path, appArea: explicitAppArea } = {}) {
			const mainStore = useMainStore()
			if (!path) {
				path = this.getPathname()
			} else {
				path = path.replace(this.basePath, '/')
			}

			const prefix = getFunnelPrefixFromPath(path)
			const changeGenderFunnelPrefix = this.getInitialPath !== prefix && prefix !== '/'
			if (force || !this.getInitialPath || changeGenderFunnelPrefix) {
				const appAreaConfig = defineFunnelByParams({
					appArea: explicitAppArea ?? this.getQueryParam('area') ?? null,
					initialPath: prefix,
					country: mainStore.getCountryCode,
					domain: this.hostname,
				})

				this.appAreaConfig = appAreaConfig
				this.initialPath = prefix
			}
		},
	},

	persist: [
		sharedKeyPersist('vuex', 'location', weeklyStorage),
		sharedKeyPersist('auth', 'location', permanentStorage, ['initialUtmParams']),
	],
})
